'use client';

import type { DetectionView } from '@terra/ui-design-system';
import { ListPageInfiniteTable } from '../../../common/ListPageInfiniteTable';
import type { EnetAssetListKind } from '../asset-list-columns';
import {
  isExternalAssetsGroupHeaderRow,
  isExternalAssetsSortColumn,
  type ExternalAssetGridItem,
  type ExternalAssetsGroupHeaderRow,
  type ExternalAssetsSort,
  type ExternalAssetsTableRow,
} from './external-assets-table.model';
import { useExternalAssetsTableColumnDefs } from './useExternalAssetsTableColumnDefs';

type ExternalAssetsTableProps = {
  listKind: EnetAssetListKind;
  rows: ExternalAssetsTableRow[];
  loadMoreRowIndex: number | null;
  sort: ExternalAssetsSort;
  detectionView: DetectionView;
  collapsedGroupKeys: ReadonlySet<string>;
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onSortChange: (sort: ExternalAssetsSort) => void;
  onToggleGroup: (groupKey: string) => void;
  onOpenAsset: (item: ExternalAssetGridItem) => void;
};

const getItemRowId = (item: ExternalAssetGridItem) => item.rowId;

export function ExternalAssetsTable({
  listKind,
  rows,
  loadMoreRowIndex,
  sort,
  detectionView,
  collapsedGroupKeys,
  isLoading,
  isFetchingMore,
  hasMore,
  onLoadMore,
  onSortChange,
  onToggleGroup,
  onOpenAsset,
}: ExternalAssetsTableProps) {
  const columnDefs = useExternalAssetsTableColumnDefs({
    listKind,
    detectionView,
    collapsedGroupKeys,
    onToggleGroup,
    onOpenAsset,
  });

  return (
    <ListPageInfiniteTable<
      ExternalAssetGridItem,
      ExternalAssetsGroupHeaderRow,
      NonNullable<ExternalAssetsSort>['colId']
    >
      tableKey={listKind}
      columnDefs={columnDefs}
      rows={rows}
      getItemRowId={getItemRowId}
      isGroupHeaderRow={isExternalAssetsGroupHeaderRow}
      loadMoreRowIndex={loadMoreRowIndex}
      sort={sort}
      isSortColumn={isExternalAssetsSortColumn}
      isLoading={isLoading}
      isFetchingMore={isFetchingMore}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      onSortChange={onSortChange}
      onToggleGroup={onToggleGroup}
      onOpenItem={onOpenAsset}
    />
  );
}
